import { Reducer } from 'redux'
import { LOAD_STATE, TEMPERATURE_TYPE } from 'apps/fleet-planner/constants/loads'
import {
  IAction,
  ILoad,
  ITemperatureSetting,
  ITruck,
  ILoadDetailsState,
  ICarrier,
  IDispatcher,
  IDriver,
  IExternalEntities,
} from 'apps/fleet-planner/types'
import {
  FETCH_LOAD_DETAILS_ACTION_TYPES,
  UPDATE_LOAD_STATUS_ACTION_TYPES,
  UPDATE_LOAD_ACTION_TYPES,
  DELETE_LOAD_ACTION_TYPES,
  DELETE_STOP_ACTION_TYPES,
  UPLOAD_ATTACHMENT_ACTION_TYPES,
  DELETE_ATTACHMENT_ACTION_TYPES,
  RESET_LOAD_DETAILS,
  UPDATE_LOAD_TRUCK_ACTION_TYPES,
  UPDATE_LOAD_TRAILER_ACTION_TYPES,
  UPDATE_LOAD_DISPATCHER_ACTION_TYPES,
  UPDATE_LOAD_DRIVERS_ACTION_TYPES,
  UPDATE_LOAD_ASSIGNMENT_ACTION_TYPES,
} from 'apps/fleet-planner/utils/actions/loadDetails'
import { getExternalEntities } from 'apps/fleet-planner/utils/isExternal'
import { externalPermissions } from 'apps/fleet-planner/utils/permissions'

const { UPDATE_LOAD_TRUCK_SUCCESS } = UPDATE_LOAD_TRUCK_ACTION_TYPES
const { UPDATE_LOAD_TRAILER_SUCCESS } = UPDATE_LOAD_TRAILER_ACTION_TYPES
const { UPDATE_LOAD_DISPATCHER_SUCCESS } = UPDATE_LOAD_DISPATCHER_ACTION_TYPES
const { UPDATE_LOAD_DRIVERS_SUCCESS } = UPDATE_LOAD_DRIVERS_ACTION_TYPES
const { UPDATE_LOAD_ASSIGNMENT_SUCCESS } = UPDATE_LOAD_ASSIGNMENT_ACTION_TYPES

const {
  FETCH_LOAD_DETAILS_FAILURE,
  FETCH_LOAD_DETAILS_REQUEST,
  FETCH_LOAD_DETAILS_SUCCESS,
} = FETCH_LOAD_DETAILS_ACTION_TYPES

const {
  UPDATE_LOAD_STATUS_FAILURE,
  UPDATE_LOAD_STATUS_REQUEST,
  UPDATE_LOAD_STATUS_SUCCESS,
} = UPDATE_LOAD_STATUS_ACTION_TYPES

const {
  UPLOAD_ATTACHMENT_FAILURE,
  UPLOAD_ATTACHMENT_REQUEST,
  UPLOAD_ATTACHMENT_SUCCESS,
} = UPLOAD_ATTACHMENT_ACTION_TYPES

const {
  DELETE_ATTACHMENT_FAILURE,
  DELETE_ATTACHMENT_REQUEST,
  DELETE_ATTACHMENT_SUCCESS,
} = DELETE_ATTACHMENT_ACTION_TYPES

const { UPDATE_LOAD_FAILURE, UPDATE_LOAD_REQUEST, UPDATE_LOAD_SUCCESS } = UPDATE_LOAD_ACTION_TYPES

const { DELETE_LOAD_FAILURE, DELETE_LOAD_REQUEST, DELETE_LOAD_SUCCESS } = DELETE_LOAD_ACTION_TYPES

const { DELETE_STOP_FAILURE, DELETE_STOP_REQUEST, DELETE_STOP_SUCCESS } = DELETE_STOP_ACTION_TYPES

interface IRequestAction extends IAction {
  payload: {
    isFleet?: boolean
  }
}

export interface ISuccessAction extends IAction {
  payload: {
    shipment: ILoad
  }
}

export interface ISuccessDriverAction extends IAction {
  payload: {
    driver: IDriver
  }
}

export interface ISuccessTruckAction extends IAction {
  payload: {
    shipment: ILoad
  }
}

export interface ISuccessTrailerAction extends IAction {
  payload: {
    shipment: ILoad
  }
}

export interface ISuccessDispatcherAction extends IAction {
  payload: {
    shipment: ILoad
  }
}

export interface ISuccessDriversAction extends IAction {
  payload: {
    shipment: ILoad
  }
}

export type LoadDetailsAction = IAction | IRequestAction | ISuccessAction

const defaultMotorCarrier: ICarrier = {
  id: 0,
  name: '',
  dotNumber: '',
}

const defaultTemperatureSetting: ITemperatureSetting = {
  temperatureType: TEMPERATURE_TYPE.fixed,
  precool: '',
  minTemperature: '',
  maxTemperature: '',
  fixedTemperature: '',
}

const defaultTruck: ITruck = {
  id: '',
  mcNumber: 0,
  vin: '',
  truckNumber: '',
}

const defaultDispatcher: IDispatcher = {
  id: '',
  firstName: '',
  lastName: '',
  fullName: '',
}

const defaultExternalEntities: IExternalEntities = {
  isExternalDispatcher: undefined,
  isExternalTrailer: undefined,
  isExternalTruck: undefined,
  isExternalDriver: undefined,
}

const defaultLoadDetails: ILoad = {
  id: '',
  status: LOAD_STATE.Pending,
  entityId: '',
  mcNumber: undefined,
  motorCarrier: defaultMotorCarrier,
  stops: [],
  trailerId: undefined,
  truckId: undefined,
  dispatcherId: '',
  notes: '',
  externalId: '',
  isSample: false,
  trailerLength: '',
  currentStopSequence: '',
  truckNumber: '',
  trailerNumber: '',
  drivers: [],
  rate: '',
  miles: '',
  temperatureSetting: defaultTemperatureSetting,
  attachments: [],
  truck: defaultTruck,
  dispatcher: defaultDispatcher,
  externalEntities: defaultExternalEntities,
  isExternal: undefined,
}

const defaultState: ILoadDetailsState = {
  loading: false,
  error: undefined,
  load: defaultLoadDetails,
  isFleet: false,
  permissions: undefined,
}

const loadDetailsReducer: Reducer<ILoadDetailsState, LoadDetailsAction> = (
  state = defaultState,
  action
) => {
  switch (action.type) {
    case UPDATE_LOAD_REQUEST:
    case UPLOAD_ATTACHMENT_REQUEST:
    case DELETE_ATTACHMENT_REQUEST:
    case UPDATE_LOAD_STATUS_REQUEST:
      return {
        ...state,
        loading: true,
        error: undefined,
      }
    case FETCH_LOAD_DETAILS_REQUEST: {
      return {
        ...state,
        loading: true,
        error: undefined,
        isFleet: false,
      }
    }
    case UPDATE_LOAD_SUCCESS:
    case UPLOAD_ATTACHMENT_SUCCESS:
    case DELETE_ATTACHMENT_SUCCESS:
    case UPDATE_LOAD_STATUS_SUCCESS: {
      const response = (action as ISuccessAction).payload.shipment
      const externalEntities = { externalEntities: getExternalEntities(response) }
      return {
        ...state,
        loading: false,
        load: response ? { ...response, ...externalEntities } : defaultLoadDetails,
      }
    }
    case FETCH_LOAD_DETAILS_SUCCESS: {
      const response = (action as ISuccessAction).payload.shipment
      const externalEntities = { externalEntities: getExternalEntities(response) }
      return {
        ...state,
        loading: false,
        load: response ? { ...response, ...externalEntities } : defaultLoadDetails,
        permissions: externalPermissions(response?.isExternal),
      }
    }
    case UPDATE_LOAD_FAILURE:
    case UPLOAD_ATTACHMENT_FAILURE:
    case DELETE_ATTACHMENT_FAILURE:
    case UPDATE_LOAD_STATUS_FAILURE: {
      return {
        ...state,
        loading: false,
        error: action.error,
      }
    }
    case FETCH_LOAD_DETAILS_FAILURE: {
      return {
        ...state,
        loading: false,
        error: action.error,
        loadDetails: defaultLoadDetails,
      }
    }
    case DELETE_LOAD_REQUEST: {
      const {
        payload: { isFleet },
      } = action as IRequestAction
      return {
        ...state,
        loading: true,
        error: undefined,
        isFleet,
      }
    }
    case DELETE_LOAD_SUCCESS: {
      return {
        ...state,
        loading: false,
        error: undefined,
      }
    }
    case DELETE_LOAD_FAILURE: {
      return {
        ...state,
        loading: false,
        error: action.error,
      }
    }
    case DELETE_STOP_REQUEST: {
      return {
        ...state,
        loading: true,
        error: undefined,
      }
    }
    case DELETE_STOP_SUCCESS: {
      return {
        ...state,
        loading: false,
        error: undefined,
        load: (action as ISuccessAction).payload.shipment ?? defaultLoadDetails,
      }
    }
    case DELETE_STOP_FAILURE: {
      return {
        ...state,
        loading: false,
        error: action.error,
      }
    }
    case RESET_LOAD_DETAILS: {
      return defaultState
    }
    case UPDATE_LOAD_TRUCK_SUCCESS: {
      const response = (action as ISuccessTruckAction)?.payload?.shipment
      const externalEntities = { externalEntities: getExternalEntities(response) }
      const { truck } = response || {}
      return {
        ...state,
        load: {
          ...state.load,
          ...externalEntities,
          truck,
          truckId: Number(truck?.id),
        },
      }
    }
    case UPDATE_LOAD_TRAILER_SUCCESS: {
      const response = (action as ISuccessTrailerAction)?.payload?.shipment
      const externalEntities = { externalEntities: getExternalEntities(response) }
      const { trailer } = response || {}
      return {
        ...state,
        load: {
          ...state.load,
          ...externalEntities,
          trailer,
          trailerId: Number(trailer?.id),
        },
      }
    }
    case UPDATE_LOAD_DISPATCHER_SUCCESS: {
      const response = (action as ISuccessDispatcherAction)?.payload?.shipment
      const externalEntities = { externalEntities: getExternalEntities(response) }
      const { dispatcher } = response || {}
      return {
        ...state,
        load: {
          ...state.load,
          ...externalEntities,
          dispatcher,
          dispatcherId: dispatcher?.id,
        },
      }
    }
    case UPDATE_LOAD_DRIVERS_SUCCESS: {
      const response = (action as ISuccessDriversAction)?.payload?.shipment
      const externalEntities = { externalEntities: getExternalEntities(response) }
      const { drivers = [] } = response || {}
      return {
        ...state,
        load: {
          ...state.load,
          ...externalEntities,
          drivers: [...drivers],
        },
      }
    }
    case UPDATE_LOAD_ASSIGNMENT_SUCCESS: {
      const response = (action as ISuccessDriversAction)?.payload?.shipment
      const externalEntities = { externalEntities: getExternalEntities(response) }
      const { drivers = [], dispatcher, motorCarrier, trailer, truck } = response || {}
      return {
        ...state,
        load: {
          ...state.load,
          ...externalEntities,
          dispatcher,
          dispatcherId: dispatcher?.id,
          drivers,
          motorCarrier,
          motorCarrierId: motorCarrier?.id,
          truck,
          truckId: Number(truck?.id),
          trailer,
          trailerId: Number(trailer?.id),
        },
      }
    }
    default:
      return state
  }
}

export default loadDetailsReducer
